import { useEffect, useMemo, useState } from 'react'
import { ForgeLabelValue, ForgeSwitch } from '@tylertech/forge-react'
import useProfile from '../hooks/useProfile'
import './General.css'

const defaultFallback = 'Not provided'

const formatPhone = (phone) => {
  if (!phone) return ''
  const digits = `${phone}`.replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  return phone
}

export default function General() {
  const { profile, isLoading, error, refetch } = useProfile()
  const [localError, setLocalError] = useState('')

  useEffect(() => {
    if (error) {
      setLocalError('Unable to load your profile right now.')
    } else {
      setLocalError('')
    }
  }, [error])

  const profileInfoFields = useMemo(
    () => [
      { label: 'Legal First Name', value: profile?.firstName, colSpan: 1 },
      { label: 'Legal Last Name', value: profile?.lastName, colSpan: 1 },
      { label: 'Preferred Name', value: profile?.preferredName, colSpan: 1 },
    ],
    [profile?.firstName, profile?.lastName, profile?.preferredName],
  )

  const contactFields = useMemo(
    () => [
      { label: 'Email Address', value: profile?.email, colSpan: 2 },
      { label: 'Phone Number', value: formatPhone(profile?.phone), colSpan: 2 },
    ],
    [profile?.email, profile?.phone],
  )

  const primaryAddressFields = useMemo(
    () => [
      { label: 'Street Address', value: profile?.addressLine1, colSpan: 4 },
      { label: 'City/Town', value: profile?.addressCity, colSpan: 1 },
      { label: 'State', value: profile?.addressState, colSpan: 1 },
      { label: 'Zip Code', value: profile?.addressZipCode, colSpan: 1 },
    ],
    [profile],
  )

  const altAddressFields = useMemo(
    () => [
      { label: 'Street Address', value: profile?.altAddressLine1, colSpan: 4 },
      { label: 'City/Town', value: profile?.altAddressCity, colSpan: 1 },
      { label: 'State', value: profile?.altAddressState, colSpan: 1 },
      { label: 'Zip Code', value: profile?.altAddressZipCode, colSpan: 1 },
    ],
    [profile?.altAddressLine1, profile?.altAddressCity, profile?.altAddressState, profile?.altAddressZipCode],
  )

  const getDisplayValue = (value, fallback = defaultFallback) => {
    if (localError) return fallback
    if (value === undefined || value === null || value === '') return fallback
    return value
  }

  const renderFields = (fields, keyPrefix = '') =>
    fields.map((field) => {
      const displayValue = getDisplayValue(field.value, field.fallback)
      const isEmpty = !isLoading && !localError && (field.value === undefined || field.value === null || field.value === '')
      const renderedValue = isLoading ? <span className="skeleton skeleton--text" aria-hidden="true" /> : displayValue
      return (
        <ForgeLabelValue
          key={`${keyPrefix}${field.label}`}
          empty={isEmpty}
          className="profile-view__field"
          style={{ '--grid-span': field.colSpan }}>
          <dt slot="label">{field.label}</dt>
          <dd slot="value">{renderedValue}</dd>
        </ForgeLabelValue>
      )
    })

  return (
    <div className="m-6">
      {error ? <p className="info-card__error">{error}</p> : null}
      {localError ? (
        <p className="info-card__error">
          {localError}{' '}
          <button type="button" className="link-button" onClick={() => refetch()}>
            Retry
          </button>
        </p>
      ) : null}

      <section className="profile-section">
        <h3 className="section-header">Profile Information</h3>
        <dl className="profile-grid mt-6">
          {renderFields(profileInfoFields)}
        </dl>
      </section>

      <section className="profile-section">
        <h3 className="section-header">Contact Details</h3>
        <dl className="profile-grid mt-6">
          {renderFields(contactFields)}
        </dl>
      </section>

      <section className="profile-section">
        <h3 className="section-header">Address</h3>

        <h4 className="forge-typography--subheading3 text-color--medium text--bold mt-6">Primary Residence Address</h4>
        <dl className="profile-grid mt-6">
          {renderFields(primaryAddressFields, 'primary-')}
        </dl>

        <div className="general__checkbox-row mt-6">
          <ForgeSwitch
            checked={!!profile?.altAddressSameAsPrimary}
            disabled>
            Address Same as Primary
          </ForgeSwitch>
        </div>

        <h4 className="forge-typography--subheading3 text-color--medium text--bold mt-6">Alternate Contact Address</h4>
        <dl className="profile-grid mt-6">
          {renderFields(altAddressFields, 'alt-')}
        </dl>
      </section>
    </div>
  )
}
